<template>
  <div ref="globeContainer" id="globecontainer">
    <!-- Hidden canvases for stackBlur processing -->
    <canvas id="origCanvas" style="display:none;"></canvas>
    <canvas id="imgCanvas" style="display:none;"></canvas>
  </div>
</template>

<script>
import * as dat from "dat.gui";

export default {
  name: "World",
  props: {
    cities: {
      type: Array,
      default: () => [
        { name: "New York", lat: 40.7128, lon: -74.0060, color: 0xff0000 },
        { name: "London", lat: 51.5074, lon: -0.1278, color: 0x00ff00 },
        { name: "Tokyo", lat: 35.6895, lon: 139.6917, color: 0x0000ff }
      ]
    }
  },
  data() {
    return {
      container: null,
      renderer: null,
      scene: null,
      camera: null,
      controls: null,
      material: null,
      globeGeo: null,
      globeMesh: null,
      light: null,
      ambientLight: null,
      pointLight: null,
      RTTs: {},
      textureMats: [],
      globeTexture: null,
      loopSteps: 50,
      normalize: false,
      vs: "",
      fs_erode: "",
      fs_dilate: "",
      fs_maximum: "",
      fs_rtt: "",
      vs_main: "",
      fs_main: "",
      mouseDown: false,
      errorRendering: false,
      currentTween: null,
      cityMarkers: [],
      heightmapImage: null,
      blurValue: 0,
      isVisible: false,
      isInitialized: false,
    };
  },
  mounted() {
    // Create intersection observer instead of immediately initializing
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.isInitialized) {
        this.isInitialized = true;
        // Load shaders and initialize scene asynchronously
        setTimeout(() => {
          this.loadShaders(() => {
            this.bootup();
          });
        }, 0);
        observer.disconnect();
      }
    }, {
      root: null,
      rootMargin: '400% 0px', // Start loading when within four viewheights of viewport
      threshold: 0
    });

    observer.observe(this.$refs.globeContainer);
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
  },
  methods: {
    rads(x) { 
      return x * Math.PI / 180; 
    },
    loadShaders(callback) {
      SHADER_LOADER.load(data => {
        this.vs = data.vs_rt.vertex;
        this.fs_erode = data.fs_erode.fragment;
        this.fs_dilate = data.fs_dilate.fragment;
        if (this.normalize) this.fs_maximum = data.fs_maximum.fragment;
        this.fs_rtt = data.fs_rtt.fragment;
        this.vs_main = data.vs_main.vertex;
        this.fs_main = data.fs_main.fragment;
        callback();
      });
    },
    bootup() {
      // Load DEM texture from /globe/img/.
      const large = window.innerWidth >= 1200;
      //const demPath = large ? "/globe/img/Srtm.2k_norm.jpg" : "/globe/img/Srtm.1k_norm.jpg";
      const demPath = "/globe/img/Srtm.2k_norm_blur3.png";
      console.log("[bootup] demPath: ", demPath);
      const globeImage = THREE.ImageUtils.loadTexture(
        demPath,
        new THREE.UVMapping(),
        () => {
          this.globeTexture = prepRTT(globeImage, this.vs, this.fs_dilate);
          this.addRTT("globe", this.globeTexture);
        }
      );
    },
    // initDatGui() {
    //   const gui = new dat.GUI();
    //   const params = {
    //     bumpScale: this.bumpScale || 97.0,
    //     // normal_offset: this.normal_offset || 1.0, // Uncomment if needed
    //     diffuse: this.diffuse || 0.0,
    //     ambient: this.ambient || 0.0,
    //     pointLight: this.pointLight || 0.5,
    //     shininess: this.shininess || 10.0,
    //     specular: this.specular || 1.0,
    //     erode: this.erode || 0.047,
    //     dilate: this.dilate || 0.05,
    //     steps: this.steps || 0,
    //     scale: this.scale || 12,
    //     lightRotate: this.lightRotate || 0.0,
    //     // blur: this.blurValue || 0
    //   };

    //   gui.add(params, 'bumpScale', 0, 100).onChange(value => {
    //     this.bumpScale = value;
    //     this.setMatUniform("bumpScale", value);
    //     this.renderScene();
    //   });

    //   // Uncomment to enable normal offset control:
    //   // gui.add(params, 'normal_offset', 0, 2).onChange(value => {
    //   //   this.normal_offset = value;
    //   //   const vec = new THREE.Vector2(value, value);
    //   //   this.setMatUniform("uNormalOffset", vec);
    //   //   this.renderScene();
    //   // });

    //   gui.add(params, 'erode', 0, 0.05).onChange(value => {
    //     this.erode = value;
    //     this.setTextureMatUniform("u_erode", value);
    //     this.tweakRTTs();
    //   });

    //   gui.add(params, 'dilate', 0, 0.05).onChange(value => {
    //     this.dilate = value;
    //     this.setTextureMatUniform("u_dilate", value);
    //     this.tweakRTTs();
    //   });

    //   gui.add(params, 'steps', 0, 100).onChange(value => {
    //     this.steps = value;
    //     this.loopSteps = value;
    //     this.tweakRTTs();
    //   });

    //   gui.add(params, 'scale', 0, 100).onChange(value => {
    //     this.scale = value;
    //     this.setMatUniform("uDisplacementPostScale", value);
    //     this.renderScene();
    //   });

    //   gui.add(params, 'diffuse', 0, 1).onChange(value => {
    //     this.diffuse = value;
    //     this.setMatUniform("tDiffuseOpacity", value);
    //     this.renderScene();
    //   });

    //   gui.add(params, 'ambient', 0, 1).onChange(value => {
    //     this.ambient = value;
    //     this.setMatUniform("uAmbientLightColor", new THREE.Color(value, value, value));
    //     this.renderScene();
    //   });

    //   gui.add(params, 'pointLight', 0, 1).onChange(value => {
    //     this.pointLight = value;
    //     this.setMatUniform("uPointLightColor", new THREE.Color(value, value, value));
    //     this.renderScene();
    //   });

    //   gui.add(params, 'shininess', 0, 250).onChange(value => {
    //     this.shininess = value;
    //     this.setMatUniform("shininess", value);
    //     this.renderScene();
    //   });

    //   gui.add(params, 'specular', 0, 10).onChange(value => {
    //     this.specular = value;
    //     this.setMatUniform("specular", new THREE.Color(value, value, value));
    //     this.renderScene();
    //   });

    //   gui.add(params, 'lightRotate', 0, 1).onChange(value => {
    //     this.lightRotate = value;
    //     // Update light rotation here if applicable, e.g.:
    //     // this.light.parent.rotation.y = value * Math.PI * 2;
    //     this.renderScene();
    //   });

    //   // gui.add(params, "blur", 0, 10).onChange(value => {
    //   //   //this.blurValue = value;
    //   //   this.applyBlurToHeightmap(value);
    //   // });

    //   gui.open();
    // },

  //   initDatGui() {
  //   const gui = new dat.GUI();
  //   const cameraParams = {
  //     posX: this.camera.position.x,
  //     posY: this.camera.position.y,
  //     posZ: this.camera.position.z,
  //     lookAtX: 120,
  //     lookAtY: 15,
  //     lookAtZ: 0,
  //     fov: this.camera.fov,
  //   };

  //   const updateCamera = () => {
  //     this.camera.position.set(cameraParams.posX, cameraParams.posY, cameraParams.posZ);
  //     this.camera.lookAt(new THREE.Vector3(cameraParams.lookAtX, cameraParams.lookAtY, cameraParams.lookAtZ));
  //     this.camera.fov = cameraParams.fov;
  //     this.camera.updateProjectionMatrix();
  //     this.renderScene();
  //   };

  //   gui.add(cameraParams, "posX", -2000, 2000).onChange(updateCamera);
  //   gui.add(cameraParams, "posY", -2000, 2000).onChange(updateCamera);
  //   gui.add(cameraParams, "posZ", -2000, 2000).onChange(updateCamera);
  //   gui.add(cameraParams, "lookAtX", -1000, 1000).onChange(updateCamera);
  //   gui.add(cameraParams, "lookAtY", -1000, 1000).onChange(updateCamera);
  //   gui.add(cameraParams, "lookAtZ", -1000, 1000).onChange(updateCamera);
  //   gui.add(cameraParams, "fov", 1, 100).onChange(updateCamera);
    
  //   gui.open();

  //   updateCamera();
  // },
  initDatGui() {
  const gui = new dat.GUI();
  // Lighting parameters
  const lightingParams = {
    ambientColor: "#404040",
    ambientIntensity: this.ambientLight.intensity || 1,
    pointColor: "#888888",
    pointIntensity: this.pointLight.intensity || 1,
    pointX: this.pointLight.position.x,
    pointY: this.pointLight.position.y,
    pointZ: this.pointLight.position.z,
    lightRotate: this.lightRotate ? this.lightRotate.rotation.y : 0
  };

  // Ambient light controls
  gui.addColor(lightingParams, "ambientColor").onChange(value => {
    this.ambientLight.color.set(value);
    this.material.uniforms.uAmbientLightColor.value.set(value);
    this.renderScene();
  });
  gui.add(lightingParams, "ambientIntensity", 0, 2).onChange(value => {
    this.ambientLight.intensity = value;
    this.renderScene();
  });

  // Point light controls
  gui.addColor(lightingParams, "pointColor").onChange(value => {
    this.pointLight.color.set(value);
    this.material.uniforms.uPointLightColor.value.set(value);
    this.renderScene();
  });
  gui.add(lightingParams, "pointIntensity", 0, 2).onChange(value => {
    this.pointLight.intensity = value;
    this.renderScene();
  });
  gui.add(lightingParams, "pointX", -1000, 1000).onChange(value => {
    this.pointLight.position.x = value;
    this.material.uniforms.uPointLightPos.value.x = value;
    this.light.position.x = value;
    this.renderScene();
  });
  gui.add(lightingParams, "pointY", -1000, 1000).onChange(value => {
    this.pointLight.position.y = value;
    this.material.uniforms.uPointLightPos.value.y = value;
    this.light.position.y = value;
    this.renderScene();
  });
  gui.add(lightingParams, "pointZ", -1000, 1000).onChange(value => {
    this.pointLight.position.z = value;
    this.material.uniforms.uPointLightPos.value.z = value;
    this.light.position.z = value;
    this.renderScene();
  });

  // Light rotation control (requires storing lightRotate on this)
  gui.add(lightingParams, "lightRotate", 0, Math.PI * 2).onChange(value => {
    if (this.lightRotate) {
      this.lightRotate.rotation.y = value;
      this.renderScene();
    }
  });

  gui.open();
},


    initComponent() {
      // Setup renderer using the parent container dimensions.
      this.container = this.$refs.globeContainer;
      try {
        this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        this.renderer.setSize(width, height);
        //this.renderer.setClearColor(0x393939, 0);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.autoClear = false;
        //this.renderer.domElement.style.backgroundColor = "transparent";
        this.container.appendChild(this.renderer.domElement);
      } catch (e) { 
        console.log("Error loading canvas for globe.", e); 
        this.errorRendering = true;
      }

      // Scene and camera.
      this.scene = new THREE.Scene();
      const aspect = this.container.clientWidth / this.container.clientHeight;
      const fov = 20;
      this.camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 10000);
      this.camera.position.x = 0;
      this.camera.position.y = 140;
      this.camera.position.z = -400;
      const lookAt = new THREE.Vector3(120, 15, 0);
      this.camera.lookAt(lookAt);
      this.scene.add(this.camera);

      // Lights.
      this.ambientLight = new THREE.AmbientLight(0x000000, 0.8); // Set the intensity of the ambient light to 1.0
      this.scene.add(this.ambientLight);
      this.pointLight = new THREE.PointLight(0x7d7d7d);
      const lightRotate = new THREE.Object3D();
      //this.pointLight.position.set(0, 200, -300);
      this.pointLight.position.set(-246, 860, -29);
      lightRotate.add(this.pointLight);
      const sphere = new THREE.SphereGeometry(100, 8, 8);
      this.light = new THREE.Mesh(sphere, new THREE.MeshBasicMaterial({ color: 0xffffff }));
      this.light.position.copy(this.pointLight.position);
      this.light.scale.set(0.05, 0.05, 0.05);
      lightRotate.add(this.light);
      this.scene.add(lightRotate);

      // Shader material.
      let uniforms = THREE.UniformsUtils.clone(THREE.ShaderLib["normalmap"].uniforms);
      const flatNormalTex = THREE.ImageUtils.loadTexture("/globe/img/flat.png", new THREE.UVMapping(), this.renderScene);
      uniforms.tNormal = { type: "t", value: flatNormalTex };
      uniforms.diffuse.value.setHex(0xffffff);
      uniforms.specular.value = new THREE.Color(1, 1, 1);
      uniforms.ambient.value.setHex(0xffffff);
      uniforms.shininess.value = 5.0;
      uniforms.enableDiffuse = { type: "i", value: 1 };
      uniforms.tDiffuse = {
        type: "t",
        // value: THREE.ImageUtils.loadTexture("/globe/img/2_no_clouds_8k.jpg", new THREE.UVMapping(), this.renderScene)
        // value: THREE.ImageUtils.loadTexture("/globe/img/world.topo.1024-2.jpg", new THREE.UVMapping(), this.renderScene)
        // value: THREE.ImageUtils.loadTexture("/globe/img/world.topo.1024.jpg", new THREE.UVMapping(), this.renderScene)
        value: THREE.ImageUtils.loadTexture("/globe/img/world.topo.4096-3.jpg", new THREE.UVMapping(), this.renderScene)
      };
      uniforms["tDisplacement"] = { type: "t", value: this.globeTexture.texture2 };
      uniforms.tDiffuseOpacity = { type: "f", value: 0.45 };
      uniforms.tDiffuse2Opacity = { type: "f", value: 0 };
      uniforms.uPointLightPos = { type: "v3", value: this.pointLight.position };
      uniforms.uPointLightColor = { type: "c", value: new THREE.Color(this.pointLight.color) };
      uniforms.uAmbientLightColor = { type: "c", value: new THREE.Color(this.ambientLight.color) };
      uniforms.matrightBottom = { type: "v2", value: new THREE.Vector2(180, -90) };
      uniforms.matleftTop = { type: "v2", value: new THREE.Vector2(-180, 90) };
      uniforms.sphereRadius = { type: "f", value: 100.0 };
      uniforms.mixAmount = { type: "f", value: 1.0 };

      uniforms["diffuse"].value.convertGammaToLinear();
      uniforms["specular"].value.convertGammaToLinear();
      uniforms["ambient"].value.convertGammaToLinear();

      uniforms.enableDisplacement = { type: "i", value: 1 };
      uniforms.uDisplacementScale = { type: "f", value: 100 };
      uniforms.uDisplacementPostScale = { type: "f", value: 12 };
      uniforms.bumpScale = { type: "f", value: 97.0 };
      uniforms.uNormalOffset = { type: "v2", value: new THREE.Vector2(1, 1) };

      this.material = new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: this.vs_main,
        fragmentShader: this.fs_main
      });

      this.globeTexture.textureMat2.uniforms.u_erode.value = 0.047;
      this.globeTexture.textureMat2.uniforms.u_dilate.value = 0.05;
      this.globeTexture.textureMat.uniforms.u_erode.value = 0.047;
      this.globeTexture.textureMat.uniforms.u_dilate.value = 0.05;

      this.textureMats = [this.globeTexture.textureMat, this.globeTexture.textureMat2];

      this.globeGeo = new THREE.PlaneGeometry(10, 10, 257, 129);
      this.globeGeo.computeTangents();
      this.globeMesh = new THREE.Mesh(this.globeGeo, this.material);
      this.globeMesh.frustumCulled = false;
      this.scene.add(this.globeMesh);

      // Controls & mouse.
      // this.controls = new THREE.TrackballControls(this.camera, this.container);
      // Object.assign(this.controls, {
      //   rotateSpeed: 3,
      //   zoomSpeed: 0,
      //   panSpeed: 0.8,
      //   dynamicDampingFactor: 0.3
      // });
      // this.controls.addEventListener("change", this.renderScene);
      this.addMouseHandler(this.renderer.domElement);

      this.animate();
      this.tweakRTTs();
      this.renderScene();

      // Setup city markers.
      this.cities.forEach(city => this.addCityMarker(city.lat, city.lon, city.color));

      // this.initDatGui();
    },
    onWindowResize() {
      if (!this.container) return;
      const width = this.container.clientWidth;
      const height = this.container.clientHeight;
      this.renderer.setSize(width, height);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderScene();
    },
    renderScene() {
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
    },
    animate() {
      requestAnimationFrame(this.animate.bind(this));
      TWEEN.update();
      // this.controls.update();
      this.renderScene();
    },
    addMouseHandler(div) {
      div.addEventListener("mousedown", () => { this.mouseDown = true; }, false);
      div.addEventListener("mouseup", () => { this.mouseDown = false; }, false);
    },
    setMatUniform(name, value) {
      this.material.uniforms[name].value = value;
    },
    setTextureMatUniform(name, value) {
      this.textureMats.forEach(mat => { mat.uniforms[name].value = value; });
    },
    tweakRTTs() {
      this.prepTextures(this.RTTs["globe"]);
    },
    prepTextures(myRTT) {
      const firstShader = this.fs_erode, secondShader = this.fs_dilate;
      myRTT.textureMat.fragmentShader = firstShader;
      myRTT.textureMat.needsUpdate = true;
      myRTT.textureMat2.fragmentShader = firstShader;
      myRTT.textureMat2.needsUpdate = true;
      myRTT.textureMat.uniforms.colorMap.value = myRTT.image;
      this.renderer.render(myRTT.scene, myRTT.camera, myRTT.texture, false);
      myRTT.textureMat.uniforms.colorMap.value = myRTT.texture2;
      for (let x = 0; x < this.loopSteps; x++) { this.calculate(myRTT); }
      myRTT.textureMat.fragmentShader = secondShader;
      myRTT.textureMat.needsUpdate = true;
      myRTT.textureMat2.fragmentShader = secondShader;
      myRTT.textureMat2.needsUpdate = true;
      for (let x = 0; x < this.loopSteps; x++) { this.calculate(myRTT); }
      this.renderScene();
    },
    calculate(myRTT) {
      this.renderer.render(myRTT.scene2, myRTT.camera2, myRTT.texture2, false);
      this.renderer.render(myRTT.scene, myRTT.camera, myRTT.texture, false);
    },
    latLongToVector3(lat, lon, radius) {
      const phi = (90 - lat) * Math.PI / 180;
      const theta = (lon + 180) * Math.PI / 180;
      return new THREE.Vector3(
        -(radius * Math.sin(phi) * Math.cos(theta)),
        radius * Math.cos(phi),
        radius * Math.sin(phi) * Math.sin(theta)
      );
    },
    addCityMarker(lat, lon, isActive = false) {
  const pos = this.latLongToVector3(lat, lon, 100);
  const color = 0xffffff; // Keep color consistent, we'll use opacity/scale for emphasis

  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(color),
    transparent: true,
    opacity: isActive ? 1.0 : 0.4
  });

  // Smaller initial size for inactive markers
  const sphereGeometry = new THREE.SphereGeometry(isActive ? 3 : 1.5, 32, 32);
  const sphere = new THREE.Mesh(sphereGeometry, material);
  sphere.position.copy(pos);
  
  // Store original scale for animations
  sphere.userData.originalScale = isActive ? 3 : 1.5;
  
  this.globeMesh.add(sphere);
  this.cityMarkers.push(sphere);
},

updateCityMarkers(activeIndex) {
  this.cityMarkers.forEach((marker, index) => {
    const isActive = index === activeIndex;
    
    // Create a TWEEN for opacity
    new TWEEN.Tween(marker.material)
      .to({ opacity: isActive ? 1.0 : 0.4 }, 500)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .start();

    // Create a TWEEN for scale
    const targetScale = isActive ? 3 : 1.5;
    new TWEEN.Tween(marker.scale)
      .to({ 
        x: targetScale / marker.userData.originalScale,
        y: targetScale / marker.userData.originalScale,
        z: targetScale / marker.userData.originalScale
      }, 500)
      .easing(TWEEN.Easing.Quadratic.InOut)
      .start();
  });
},

  //   addCityMarker(lat, lon, isActive = false) {
  //   const pos = this.latLongToVector3(lat, lon, 100);

  //   const color = isActive ? 0x008080 : 0xffffff; // White if active, muted teal otherwise

  //   const material = new THREE.MeshBasicMaterial({
  //     color: new THREE.Color(color),
  //     transparent: true,
  //     opacity: 0.8
  //   });

  //   const sphereGeometry = new THREE.SphereGeometry(3, 32, 32);
  //   const sphere = new THREE.Mesh(sphereGeometry, material);
  //   sphere.position.copy(pos);
  //   this.scene.add(sphere);

  //   // Store the marker mesh for later updates
  //   this.cityMarkers.push(sphere);
  // },
  moveToCity(lat, lon) {
    if (!this.globeMesh) {
    // Defer the call until after next DOM update cycle
    //this.$nextTick(() => this.moveToCity(lat, lon));
    console.log("[moveToCity] globeMesh not ready yet");
    return;
  }
  // Get city's local position on the globe (radius = 100)
  const cityPos = this.latLongToVector3(lat, lon, 100);
  // Compute the city's local angle in the XZ plane.
  const cityAngle = Math.atan2(cityPos.x, cityPos.z);

  // Define desired look direction (from dat.gui values)
  // For a fixed camera, desired look-at in world space; here we use your starting values.
  const desiredLookAt = new THREE.Vector3(120, 15, 0);
  const desiredAngle = Math.atan2(desiredLookAt.x, desiredLookAt.z);

  // To align the city with the camera's look, set:
  // (cityAngle + targetGlobeRotation) = desiredAngle
  // add 15 degrees to the targetYRotation
  const targetYRotation = desiredAngle - cityAngle + (55 * Math.PI / 180);
  console.log("[moveToCity] current rotation: ", this.globeMesh.rotation.y);
  console.log("[moveToCity] targetYRotation: ", targetYRotation);

  // Tween globeMesh.rotation.y to targetYRotation
  const initialY = this.globeMesh.rotation.y;
  const tweenObj = { t: 0 };
  new TWEEN.Tween(tweenObj)
    .to({ t: 1 }, 2000)
    .easing(TWEEN.Easing.Quadratic.InOut)
    .onUpdate(() => {
      this.globeMesh.rotation.y = initialY + (targetYRotation - initialY) * tweenObj.t;
      this.renderScene();
    })
    .start();

  // Optionally update markers.
  const cityIndex = this.cities.findIndex(city => city.lat === lat && city.lon === lon);
  if (cityIndex !== -1) {
    this.updateCityMarkers(cityIndex);
  }
},


  // moveToCity(lat, lon) {
  //   // City's position on an unrotated globe (radius 100)
  //   let cityLocal = this.latLongToVector3(lat, lon, 100);
  //   // Current world position given globeMesh.quaternion
  //   let currentCityWorld = cityLocal.clone().applyQuaternion(this.globeMesh.quaternion);
  //   // Define the desired target position on the sphere.
  //   // Here, we pick a vector that, when multiplied by 100, lies in the top left of the view.
  //   let targetVec = new THREE.Vector3(-0.5, 0.5, 1).normalize().multiplyScalar(100);
    
  //   // Compute a quaternion that rotates currentCityWorld to targetVec.
  //   let qDelta = new THREE.Quaternion().setFromUnitVectors(
  //     currentCityWorld.clone().normalize(),
  //     targetVec.clone().normalize()
  //   );
  //   // New target quaternion: apply the delta to the globe's current rotation.
  //   let targetQuat = qDelta.multiply(this.globeMesh.quaternion.clone());

  //   // Tween globeMesh.quaternion from current to target using slerp.
  //   let initialQuat = this.globeMesh.quaternion.clone();
  //   let tweenObj = { t: 0 };
  //   new TWEEN.Tween(tweenObj)
  //     .to({ t: 1 }, 2000)
  //     .easing(TWEEN.Easing.Quadratic.InOut)
  //     .onUpdate(() => {
  //       THREE.Quaternion.slerp(initialQuat, targetQuat, this.globeMesh.quaternion, tweenObj.t);
  //     })
  //     .start();

  //   // Update markers to reflect the active city.
  //   const cityIndex = this.cities.findIndex(city => city.lat === lat && city.lon === lon);
  //   if (cityIndex !== -1) {
  //     this.updateCityMarkers(cityIndex);
  //   }
  // },

    // moveToCity(lat, lon) {
    //   if (!this.camera) { 
    //     console.log("[moveToCity] not ready yet"); 
    //     return; 
    //   }

    //   const target = this.latLongToVector3(lat, lon, 1000);
    //   this.currentTween = new TWEEN.Tween(this.camera.position)
    //     .to({ x: target.x, y: target.y, z: target.z }, 2000)
    //     .easing(TWEEN.Easing.Quadratic.InOut)
    //     .onUpdate(() => { 
    //       this.camera.lookAt(this.scene.position); 
    //     })
    //     .start();

    //   const cityIndex = this.cities.findIndex(city => city.lat === lat && city.lon === lon);
    //   if (cityIndex !== -1) {
    //     this.updateCityMarkers(cityIndex);
    //   }
    // },
    moveToCityByUser(cityIndex) {
      const city = this.cities[cityIndex];
      console.log("[World] move to: ", city);
      if (this.currentTween) {
        this.currentTween.stop();
      }
      this.moveToCity(city.lat, city.lon);

      // Update city markers' colors
      this.updateCityMarkers(cityIndex);
    },
    addRTT(name, texture) {
      this.RTTs[name] = texture;
      if (Object.keys(this.RTTs).length === 1) {
        if (this.normalize) {
          const normScene = new THREE.Scene();
          const normTexture = new THREE.WebGLRenderTarget(1, 1);
          const normTextureMat = new THREE.ShaderMaterial({
            uniforms: {
              colorMap: { value: texture.image },
              u_divisor: { value: 1.0 },
              u_textureSize: { value: new THREE.Vector2(1, 1) }
            },
            vertexShader: this.vs,
            fragmentShader: this.fs_maximum
          });
          const normCamera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 1, 10000);
          const normTextureMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), normTextureMat);
          normScene.add(normTextureMesh);
        }
        this.initComponent();
      }
    }
  }
};
</script>

<style>
#globecontainer {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.dg.ac {
    position: fixed;
    top: 0;
    left: 0;
    right: 400px;
    height: 0;
    z-index: 1000;
}
</style>
